/**
 * Affiliate signup + approval queue — data-layer client.
 *
 * Thin typed wrapper over `apiClient` for the three API surfaces this page
 * talks to:
 *   - /api/v1/affiliate-signup         (apps/api/src/routes/affiliate-signup.ts)
 *   - /api/v1/affiliate-approval       (apps/api/src/routes/affiliate-approval.ts)
 *   - /api/v1/affiliate-referral       (apps/api/src/routes/affiliate-referral.ts)
 *
 * Keep this file dependency-free beyond `apiClient` — no React, no TanStack,
 * so the KPI derivation and filter logic stays unit-testable in isolation.
 */
import { apiClient, ApiError } from "../../api-client";

// ---------------------------------------------------------------------------
// Types — mirrors the backend row shape. The `/queue` endpoint emits these
// columns directly (see apps/api/src/routes/affiliate-approval.ts:97). Keep
// these optional-where-nullable to tolerate older rows written before the
// brand column additions.
// ---------------------------------------------------------------------------

export type ApprovalStatus = "pending" | "approved" | "rejected";

export const APPROVAL_STATUSES: readonly ApprovalStatus[] = [
	"pending",
	"approved",
	"rejected",
] as const;

export type QueueFilter = ApprovalStatus | "all";

export const QUEUE_FILTERS: readonly QueueFilter[] = [
	"pending",
	"approved",
	"rejected",
	"all",
] as const;

export function isQueueFilter(v: unknown): v is QueueFilter {
	return typeof v === "string" && (QUEUE_FILTERS as readonly string[]).includes(v);
}

export interface QueueRow {
	id: string;
	tenant_id: string;
	email: string;
	name: string | null;
	company: string | null;
	country: string | null;
	traffic_source: string | null;
	status: ApprovalStatus;
	created_at: number;
	reviewed_by: string | null;
	reviewed_at: number | null;
	rejection_reason: string | null;
}

export interface QueueResponse {
	count: number;
	filters: {
		status: string;
		country: string | null;
		traffic_source: string | null;
		limit: number;
	};
	items: QueueRow[];
}

export interface ApproveResponse {
	id: string;
	status: "approved";
	reviewed_by: string | null;
	reviewed_at: number;
}

export interface RejectResponse {
	id: string;
	status: "rejected";
	reviewed_by: string | null;
	reviewed_at: number;
	rejection_reason: string;
}

export interface ReferralCodeRow {
	code: string;
	affiliate_id: string;
	tenant_id?: string;
	created_at?: number;
}

// ---------------------------------------------------------------------------
// KPI derivation — the /queue endpoint returns the full filtered set, which
// is enough to compute today/week/pending/rejected counts client-side. We
// keep the function pure so the test file can feed it hand-rolled rows.
//
// "today" = unix-ms window of the local midnight.
// "week"  = last rolling 7d (not ISO week, matches operator intuition).
// ---------------------------------------------------------------------------

export interface SignupStats {
	today: number;
	week: number;
	pending: number;
	rejected: number;
}

export function computeSignupStats(
	rows: readonly QueueRow[],
	nowMs: number = Date.now(),
): SignupStats {
	const nowSec = Math.floor(nowMs / 1000);
	const dayStart = Math.floor(nowMs / 86_400_000) * 86_400;
	const weekStart = nowSec - 7 * 86_400;

	let today = 0;
	let week = 0;
	let pending = 0;
	let rejected = 0;

	for (const row of rows) {
		if (row.created_at >= dayStart) today += 1;
		if (row.created_at >= weekStart) week += 1;
		if (row.status === "pending") pending += 1;
		if (row.status === "rejected") rejected += 1;
	}

	return { today, week, pending, rejected };
}

// ---------------------------------------------------------------------------
// Remote bindings — TanStack Query consumers import these directly.
// ---------------------------------------------------------------------------

const APPROVAL_BASE = "/affiliate-approval";
const REFERRAL_BASE = "/affiliate-referral";

export interface FetchQueueArgs {
	status: QueueFilter;
	limit?: number;
}

/**
 * GET /api/v1/affiliate-approval/queue — returns rows for the chosen status
 * filter. "all" is passed through untouched to the API, which maps it to a
 * `1=1` WHERE clause server-side.
 */
export async function fetchApprovalQueue(
	args: FetchQueueArgs,
): Promise<QueueResponse> {
	return apiClient.get<QueueResponse>(`${APPROVAL_BASE}/queue`, {
		query: { status: args.status, limit: args.limit ?? 200 },
	});
}

/** POST /api/v1/affiliate-approval/:id/approve */
export async function approveAffiliate(id: string): Promise<ApproveResponse> {
	return apiClient.post<ApproveResponse>(
		`${APPROVAL_BASE}/${encodeURIComponent(id)}/approve`,
		{},
	);
}

export interface RejectAffiliateArgs {
	id: string;
	reason: string;
}

/** POST /api/v1/affiliate-approval/:id/reject */
export async function rejectAffiliate(
	args: RejectAffiliateArgs,
): Promise<RejectResponse> {
	return apiClient.post<RejectResponse>(
		`${APPROVAL_BASE}/${encodeURIComponent(args.id)}/reject`,
		{ reason: args.reason },
	);
}

/**
 * GET /api/v1/affiliate-referral — tenant referral list. We surface the
 * first row's code as the "shareable referral code" for the readonly field.
 * Returns `null` when the tenant has no referral codes minted yet.
 *
 * Spec shorthand calls this `GET /affiliate-referral/code`. There is no such
 * literal endpoint on the backend — we normalize by listing the tenant's
 * referrals and extracting the most recent row.
 */
export async function fetchReferralCode(): Promise<ReferralCodeRow | null> {
	try {
		const rows = await apiClient.get<unknown>(REFERRAL_BASE);
		if (!Array.isArray(rows) || rows.length === 0) return null;
		// Pick the first row that exposes a `.code` string. The API surface
		// varies slightly between the code-store and redeemed-referrals shapes;
		// both include `code`, so a field-level filter is safest.
		for (const raw of rows) {
			if (
				raw &&
				typeof raw === "object" &&
				"code" in raw &&
				typeof (raw as { code?: unknown }).code === "string"
			) {
				const obj = raw as ReferralCodeRow & Record<string, unknown>;
				return {
					code: obj.code,
					affiliate_id:
						typeof obj.affiliate_id === "string" ? obj.affiliate_id : "",
					tenant_id:
						typeof obj.tenant_id === "string" ? obj.tenant_id : undefined,
					created_at:
						typeof obj.created_at === "number" ? obj.created_at : undefined,
				};
			}
		}
		return null;
	} catch (err) {
		if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
			return null;
		}
		throw err;
	}
}

// ---------------------------------------------------------------------------
// Presentation helpers — pure + tested.
// ---------------------------------------------------------------------------

export function formatInteger(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return new Intl.NumberFormat("en-US").format(Math.trunc(n));
}

export function formatSignupDate(unixSec: number): string {
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "—";
	const d = new Date(unixSec * 1000);
	return d.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

export const approvalQueryKeys = {
	all: ["affiliate-approvals"] as const,
	queue: (status: QueueFilter) =>
		[...approvalQueryKeys.all, "queue", status] as const,
	referralCode: () => [...approvalQueryKeys.all, "referral-code"] as const,
};
